import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

import { Poller } from "./poller";
import { PollType, StatusOptionType } from "./type";
import { getLoadingOption, getPollInterval } from "./utils";

export type GroupCycleInfo = {
  pollers?: Map<string, Poller<unknown>>;
  lastUpdated: Date;
};

type PollerGroupOptions = {
  interval?: number;
  onError?: (e: unknown) => void;
  onLoadingChange?: (
    status: StatusOptionType,
    timeFrame: TimeFrameType
  ) => void;
  onCycle?: (option: GroupCycleInfo) => void;
};

export default class PollerGroup {
  private pollers = new Map<string, Poller<unknown>>();
  private pollInterval: NodeJS.Timeout | null = null;
  private timeFrame: TimeFrameType = {};
  private intervalMs: number;
  private onCycle?: (option: GroupCycleInfo) => void;
  private onLoadingChange?: (
    status: StatusOptionType,
    timeFrame: TimeFrameType
  ) => void;
  private onError?: (e: unknown) => void;

  constructor(options?: PollerGroupOptions) {
    this.intervalMs = options?.interval || Infinity;
    this.onCycle = options?.onCycle;
    this.onLoadingChange = options?.onLoadingChange;
    this.onError = options?.onError;
  }

  pause() {
    this.unsubscribe();
  }

  resume(type: PollType = "polling") {
    if (this.timeFrame.id) this.startPolling(this.timeFrame, type);
  }

  private async startCycle(type: PollType, t?: TimeFrameType) {
    if (!this.pollers.size) return;
    try {
      this.setLoading(true, type);
      const pollers = Array.from(this.pollers.values());
      const promises = pollers.map(p =>
        p.executeWith(t ?? this.timeFrame, type)
      );
      await Promise.allSettled(promises);
    } catch (error) {
      this.onError?.(error);
    } finally {
      const lastUpdated = new Date();
      const pollers = this.pollers;
      this.onCycle?.({ lastUpdated, pollers });
      this.setLoading(false, type);
    }
  }

  register(id: string, poller: Poller) {
    poller && this.pollers.set(id, poller);
  }

  registerOnce(id: string, poller: Poller, pollImmediate: boolean = true) {
    const exist = this.pollers.get(id);
    if (!exist) {
      this.register(id, poller);
    }
    this.pausePoller(id, !pollImmediate);
  }

  async startPolling(
    timeFrame: TimeFrameType,
    type: PollType = "refresh",
    intervalMs?: number
  ) {
    this.unsubscribe();
    this.timeFrame = timeFrame;
    this.intervalMs = intervalMs || getPollInterval(timeFrame).milliseconds;
    this.startCycle(type, timeFrame);
    this.pollInterval = setInterval(
      () => this.startCycle("polling", timeFrame),
      this.intervalMs
    );
  }

  async pollOnce(timeFrame: TimeFrameType) {
    this.unsubscribe();
    this.timeFrame = timeFrame;
    this.startCycle("refresh", timeFrame);
  }

  unsubscribe() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  private setLoading(isLoading: boolean, type: PollType) {
    const status = getLoadingOption({ isLoading, type });
    this.onLoadingChange?.(status, this.timeFrame);
  }

  pausePoller(pollerId: string, isPaused: boolean) {
    const p = this.pollers.get(pollerId);
    p?.skipCycles(isPaused);
    if (p?.requiresRefresh()) {
      const initialRunPending = p.hasRunEver();
      p.executeWith(this.timeFrame, initialRunPending ? "refresh" : "polling");
    }
  }

  unregister(pollerId: string) {
    this.pollers.get(pollerId)?.unsubscribe();
    this.pollers.delete(pollerId);
  }
}
